import type { Consult, EzyVetEpoch, InvoiceLineItem } from "@/types/ezyvet";

/**
 * Consult fields holding clinical-record text, in SOAP order. Mirrors
 * SOAP_FIELDS in backend/app/integrations/ezyvet/mappers.py: keep the two in step.
 */
export const SOAP_FIELDS = [
  { key: "subjective", label: "Subjective", fields: ["subjective", "history", "presenting_problem"] },
  { key: "objective", label: "Objective", fields: ["objective", "physical_exam", "examination"] },
  { key: "assessment", label: "Assessment", fields: ["assessment", "diagnosis"] },
  { key: "plan", label: "Plan", fields: ["plan", "treatment_plan"] },
  { key: "notes", label: "Notes", fields: ["clinical_notes", "notes", "comments"] },
] as const;

export type SoapKey = (typeof SOAP_FIELDS)[number]["key"];

export interface SoapSection {
  key: SoapKey;
  label: string;
  text: string | null;
}

function firstText(record: Record<string, unknown>, names: readonly string[]): string | null {
  for (const name of names) {
    const value = record[name];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return null;
}

/** S, O, A and P always (empty ones as null, so the layout is stable); Notes only when present. */
export function soapSections(consult: Consult): SoapSection[] {
  return SOAP_FIELDS.map(({ key, label, fields }) => ({ key, label, text: firstText(consult, fields) })).filter(
    (s) => s.key !== "notes" || s.text,
  );
}

export function toNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

export function asId(value: unknown): string | null {
  return value === null || value === undefined || value === "" || value === 0 || value === "0" ? null : String(value);
}

export interface InvoiceRow {
  id: string;
  code: string | null;
  description: string;
  quantity: number | null;
  unitPrice: number | null;
  discount: number | null;
  total: number | null;
}

/** One invoice line in display form. Mirrors mappers.to_invoice_line. */
export function toInvoiceRow(line: InvoiceLineItem, index: number): InvoiceRow {
  const quantity = toNumber(line.quantity);
  const unitPrice = toNumber(line.price ?? line.unit_price ?? line.price_ex_tax);
  const total = toNumber(line.total ?? line.line_total ?? line.total_ex_tax);
  return {
    id: asId(line.id) ?? `row-${index}`,
    code: line.product_code ? String(line.product_code) : asId(line.product_id),
    description: firstText(line, ["product_name", "name", "description"]) ?? "Unnamed item",
    quantity,
    unitPrice,
    discount: toNumber(line.discount),
    total: total ?? (quantity !== null && unitPrice !== null ? quantity * unitPrice : null),
  };
}

export function epochToDate(value: EzyVetEpoch | null | undefined): Date | null {
  const n = toNumber(value);
  return n && n > 0 ? new Date(n * 1000) : null;
}

const usd = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });
export function formatUsd(value: number | null | undefined): string {
  return value === null || value === undefined ? "—" : usd.format(value);
}

/** "4h 12m", "12m 05s", "45s"; zero or less is "expired". */
export function formatDuration(seconds: number): string {
  if (seconds <= 0) return "expired";
  const s = Math.floor(seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const rest = s % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${String(rest).padStart(2, "0")}s`;
  return `${rest}s`;
}

// ---------------------------------------------------------------------------
// Highlighting
// ---------------------------------------------------------------------------

const STOP_WORDS = new Set([
  "a", "an", "and", "at", "for", "from", "in", "of", "on", "or", "the", "to", "with", "x", "per", "was", "were",
  "given", "performed", "administered", "completed", "done", "test", "patient",
]);

/**
 * The phrases to highlight for a flag: the detected terms the backend sent,
 * else the meaningful words of the flag's description.
 */
export function highlightTerms(eventDescription: string, detected: string[] | undefined): string[] {
  const source = detected?.length ? detected : eventDescription.split(/[^\p{L}\p{N}/+-]+/u);
  const terms = source
    .map((t) => t.trim())
    .filter((t) => t.length >= 3 && !STOP_WORDS.has(t.toLowerCase()));
  // Longest first so "CBC panel" wins over "CBC" in the alternation
  return [...new Set(terms)].sort((a, b) => b.length - a.length);
}

export interface TextSegment {
  text: string;
  match: boolean;
}

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** Splits text into plain and matching segments (case-insensitive, whole-word starts). */
export function splitHighlights(text: string, terms: string[]): TextSegment[] {
  if (!terms.length || !text) return [{ text, match: false }];
  const pattern = new RegExp(`(?<![\\p{L}\\p{N}])(${terms.map(escapeRegExp).join("|")})`, "giu");
  const segments: TextSegment[] = [];
  let last = 0;
  for (const m of text.matchAll(pattern)) {
    const start = m.index ?? 0;
    if (start > last) segments.push({ text: text.slice(last, start), match: false });
    segments.push({ text: m[0], match: true });
    last = start + m[0].length;
  }
  if (last < text.length) segments.push({ text: text.slice(last), match: false });
  return segments;
}
